import { useState, useEffect, useCallback } from "react";
import {
  Save, Loader2, Database, Download, RefreshCw, CheckCircle2, ShieldAlert,
  Archive, FileJson, Clock, HardDrive, Trash2, AlertTriangle, Check
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { toast } from "sonner";

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}

export default function Settings() {
  const { reloadSettings } = useAuth();
  const [s, setS] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [backups, setBackups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [backupRunning, setBackupRunning] = useState(false);

  // Fresh Start / Reset States
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetConfirmInput, setResetConfirmInput] = useState("");
  const [resetting, setResetting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [resS, resT, resB] = await Promise.all([
        api.get("/settings"),
        api.get("/whatsapp-templates"),
        api.get("/settings/backups").catch(() => ({ data: { items: [] } })),
      ]);
      setS(resS.data);
      setTemplates(resT.data.items || []);
      setBackups(resB.data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const setNum = (k, v) => set(k, v === "" ? "" : Number(v));

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { data: s });
      toast.success("Settings saved");
      reloadSettings();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveTemplates = async () => {
    setSaving(true);
    try {
      await api.put("/whatsapp-templates", { templates });
      toast.success("Templates saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Run On-demand Backup
  const runBackupSnapshot = async () => {
    setBackupRunning(true);
    try {
      const res = await api.post("/settings/backup/run");
      toast.success("Backup snapshot created successfully!");
      setBackups((prev) => [res.data, ...prev]);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBackupRunning(false);
    }
  };

  // Export raw JSON
  const handleExportData = (coll) => {
    const url = `${api.defaults.baseURL || ""}/settings/backup/export?collection=${coll}`;
    window.open(url, "_blank");
  };

  // Execute Fresh Start Reset
  const handleExecuteReset = async () => {
    if (resetConfirmInput.trim().toUpperCase() !== "RESET") {
      toast.error("Please type RESET in the box to confirm");
      return;
    }
    setResetting(true);
    try {
      const res = await api.post("/settings/data-reset", { confirm_text: "RESET" });
      toast.success(res.data.message || "Fresh start reset completed successfully!");
      setResetDialogOpen(false);
      setResetConfirmInput("");
      loadData();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setResetting(false);
    }
  };

  if (!s) return <Loading />;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Settings"
        subtitle="Clinic configuration, business rules, WhatsApp templates, and backup recovery"
        testid="settings-page"
      />

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid grid-cols-2 sm:grid-cols-5 w-full max-w-3xl bg-slate-100 p-1 gap-1">
          <TabsTrigger value="general" data-testid="tab-general" className="text-xs font-semibold">
            General
          </TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules" className="text-xs font-semibold">
            Business Rules
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates" className="text-xs font-semibold">
            WhatsApp
          </TabsTrigger>
          <TabsTrigger value="backups" data-testid="tab-backups" className="text-xs font-semibold">
            Backup & Export
          </TabsTrigger>
          <TabsTrigger
            value="reset"
            data-testid="tab-reset"
            className="text-xs font-bold text-red-600 data-[state=active]:bg-red-600 data-[state=active]:text-white flex items-center gap-1 justify-center"
          >
            <Trash2 className="h-3.5 w-3.5" /> Fresh Start / Reset
          </TabsTrigger>
        </TabsList>

        {/* 1. General Tab */}
        <TabsContent value="general" className="mt-4">
          <Card className="p-5 grid sm:grid-cols-2 gap-4 border-slate-200 bg-white">
            <Field label="Clinic Name"><Input value={s.clinic_name || ""} onChange={(e) => set("clinic_name", e.target.value)} data-testid="set-clinic-name" /></Field>
            <Field label="Phone"><Input value={s.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label="WhatsApp"><Input value={s.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} /></Field>
            <Field label="City"><Input value={s.city || ""} onChange={(e) => set("city", e.target.value)} /></Field>
            <Field label="Address"><Input value={s.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
            <Field label="Google Maps URL"><Input value={s.google_maps_url || ""} onChange={(e) => set("google_maps_url", e.target.value)} /></Field>
            <Field label="Google Review URL"><Input value={s.google_review_url || ""} onChange={(e) => set("google_review_url", e.target.value)} /></Field>
            <Field label="Timezone"><Input value={s.timezone || "Asia/Kolkata"} disabled /></Field>
          </Card>
          <div className="mt-4">
            <Button onClick={saveSettings} disabled={saving} data-testid="save-general" className="bg-bk-green hover:bg-emerald-700 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save Settings
            </Button>
          </div>
        </TabsContent>

        {/* 2. Business Rules Tab */}
        <TabsContent value="rules" className="mt-4">
          <Card className="p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 border-slate-200 bg-white">
            <Field label="Default Clinic Fee (₹)"><Input type="number" value={s.default_clinic_fee} onChange={(e) => setNum("default_clinic_fee", e.target.value)} /></Field>
            <Field label="Default Home Fee (₹)"><Input type="number" value={s.default_home_fee} onChange={(e) => setNum("default_home_fee", e.target.value)} /></Field>
            <Field label="Attendance Cutoff (HH:MM)"><Input value={s.attendance_cutoff} onChange={(e) => set("attendance_cutoff", e.target.value)} data-testid="set-cutoff" /></Field>
            <Field label="Low Advance (sessions)"><Input type="number" value={s.low_advance_sessions} onChange={(e) => setNum("low_advance_sessions", e.target.value)} /></Field>
            <Field label="Follow-up Absence Threshold"><Input type="number" value={s.consecutive_absence_followup} onChange={(e) => setNum("consecutive_absence_followup", e.target.value)} /></Field>
            <Field label="High Priority Threshold"><Input type="number" value={s.consecutive_absence_high} onChange={(e) => setNum("consecutive_absence_high", e.target.value)} /></Field>
            <Field label="Owner Share %"><Input type="number" value={s.settlement_owner_pct} onChange={(e) => setNum("settlement_owner_pct", e.target.value)} data-testid="set-owner-pct" /></Field>
            <Field label="Physio Share %"><Input type="number" value={s.settlement_physio_pct} onChange={(e) => setNum("settlement_physio_pct", e.target.value)} data-testid="set-physio-pct" /></Field>
            <Field label="Petrol Owner %"><Input type="number" value={s.petrol_owner_pct} onChange={(e) => setNum("petrol_owner_pct", e.target.value)} /></Field>
            <Field label="Petrol Physio %"><Input type="number" value={s.petrol_physio_pct} onChange={(e) => setNum("petrol_physio_pct", e.target.value)} /></Field>
          </Card>
          <div className="mt-4">
            <Button onClick={saveSettings} disabled={saving} data-testid="save-rules" className="bg-bk-green hover:bg-emerald-700 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save Rules
            </Button>
          </div>
        </TabsContent>

        {/* 3. WhatsApp Templates Tab */}
        <TabsContent value="templates" className="mt-4">
          <div className="space-y-3">
            {templates.map((t, i) => (
              <Card key={t.key} className="p-4 border-slate-200 bg-white">
                <Label className="font-semibold">{t.name}</Label>
                <Textarea rows={3} value={t.message} onChange={(e) => setTemplates((x) => x.map((y, j) => j === i ? { ...y, message: e.target.value } : y))} className="mt-2" data-testid={`tpl-${t.key}`} />
                <p className="text-xs text-slate-400 mt-1">Use {"{name}"} for patient name{t.key === "review_request" ? ", {review_url} for review link" : ""}.</p>
              </Card>
            ))}
          </div>
          <div className="mt-4">
            <Button onClick={saveTemplates} disabled={saving} data-testid="save-templates" className="bg-bk-green hover:bg-emerald-700 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save Templates
            </Button>
          </div>
        </TabsContent>

        {/* 4. Backup & Disaster Recovery Tab */}
        <TabsContent value="backups" className="mt-4 space-y-4">
          {/* Action Header Card */}
          <Card className="p-5 border-slate-200 bg-slate-50/70 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="font-display font-bold text-slate-900 text-base flex items-center gap-2">
                <HardDrive className="h-5 w-5 text-bk-blue" />
                Database Backup & Export Suite
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Create immutable snapshot records or download raw JSON backups for all critical collections.
              </p>
            </div>
            <Button
              onClick={runBackupSnapshot}
              disabled={backupRunning}
              className="bg-bk-blue hover:bg-blue-700 text-white gap-1.5 shadow-sm"
            >
              {backupRunning ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Database className="h-4 w-4" />
              )}
              Create Backup Snapshot
            </Button>
          </Card>

          {/* Collection Direct JSON Exports */}
          <Card className="p-5 border-slate-200 bg-white">
            <h4 className="font-bold text-sm text-slate-800 mb-3 flex items-center gap-2">
              <Download className="h-4 w-4 text-emerald-600" />
              Direct Collection JSON Exports
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
              {[
                { key: "patients", label: "Patients" },
                { key: "attendance", label: "Attendance" },
                { key: "ledger", label: "Ledger Accounts" },
                { key: "payments", label: "Payments" },
                { key: "home_visits", label: "Home Visits" },
                { key: "deposits", label: "Deposits" },
                { key: "expenses", label: "Expenses" },
                { key: "settlements", label: "Settlements" },
                { key: "audit_logs", label: "Audit Logs" },
              ].map((c) => (
                <Button
                  key={c.key}
                  size="sm"
                  variant="outline"
                  onClick={() => handleExportData(c.key)}
                  className="justify-start text-xs h-9 bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-700 gap-2"
                >
                  <FileJson className="h-4 w-4 text-bk-blue shrink-0" />
                  <span className="truncate">{c.label}</span>
                </Button>
              ))}
            </div>
          </Card>

          {/* Backup Snapshot History Table */}
          <Card className="overflow-hidden border-slate-200 bg-white">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h4 className="font-bold text-sm text-slate-800 flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-400" />
                Snapshot History
              </h4>
              <Badge variant="outline" className="text-xs">
                {backups.length} Snapshots
              </Badge>
            </div>
            {backups.length === 0 ? (
              <div className="p-8">
                <EmptyState
                  icon={Archive}
                  title="No backup snapshots yet"
                  subtitle="Click 'Create Backup Snapshot' to generate a point-in-time database snapshot."
                />
              </div>
            ) : (
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead className="text-xs font-bold">Snapshot Date / Time</TableHead>
                    <TableHead className="text-xs font-bold">Created By</TableHead>
                    <TableHead className="text-xs font-bold">Patients</TableHead>
                    <TableHead className="text-xs font-bold">Attendance</TableHead>
                    <TableHead className="text-xs font-bold">Ledger</TableHead>
                    <TableHead className="text-xs font-bold">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backups.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell className="font-mono text-xs text-slate-800 font-semibold">
                        {b.created_at ? new Date(b.created_at).toLocaleString("en-IN") : "-"}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{b.created_by}</TableCell>
                      <TableCell className="text-xs font-mono text-slate-700">{b.stats?.patients ?? "-"}</TableCell>
                      <TableCell className="text-xs font-mono text-slate-700">{b.stats?.attendance ?? "-"}</TableCell>
                      <TableCell className="text-xs font-mono text-slate-700">{b.stats?.ledger ?? "-"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                          {b.status || "Success"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* 5. Fresh Start / Reset Data Tab */}
        <TabsContent value="reset" className="mt-4 space-y-4">
          <Card className="p-6 border-red-300 bg-red-50/50 shadow-sm">
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-red-200 pb-4">
                <div>
                  <h3 className="font-display font-bold text-red-900 text-lg flex items-center gap-2">
                    <Trash2 className="h-5 w-5 text-red-600" />
                    Fresh Start: Reset Operational Test Data
                  </h3>
                  <p className="text-xs text-red-700 mt-1">
                    Clears all erroneous test attendance, payments, ledger entries, and follow-ups while <strong>preserving 100% of all registered patients, assigned physiotherapists, and staff accounts</strong>.
                  </p>
                </div>
                <Button
                  onClick={() => {
                    setResetConfirmInput("");
                    setResetDialogOpen(true);
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold px-4 py-2 text-xs shadow gap-1.5 shrink-0"
                >
                  <Trash2 className="h-4 w-4" /> Start Fresh / Reset Data
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                {/* Kept Box */}
                <div className="bg-white p-4 rounded-lg border border-emerald-300 shadow-xs space-y-2">
                  <p className="font-bold text-emerald-900 text-sm flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    KEPT & PRESERVED (100% Real Master Data)
                  </p>
                  <p className="text-[11px] text-slate-500">
                    This real data is NEVER deleted or modified:
                  </p>
                  <ul className="space-y-1.5 text-slate-700 pt-1">
                    <li className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      <strong>All Registered Patients</strong> (Clinic & Home)
                    </li>
                    <li className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      <strong>Physiotherapist Mappings</strong> (Which patient is assigned to which physio)
                    </li>
                    <li className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      <strong>Contact Info</strong> (Phone numbers, WhatsApp, Addresses, Chief complaint)
                    </li>
                    <li className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      <strong>Staff & Physio Accounts</strong> (Admin, Receptionist, all 10+ Physiotherapists)
                    </li>
                    <li className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      <strong>Clinic Settings & Fees</strong> (Default fees, Google review link, templates)
                    </li>
                  </ul>
                </div>

                {/* Cleared Box */}
                <div className="bg-white p-4 rounded-lg border border-red-300 shadow-xs space-y-2">
                  <p className="font-bold text-red-900 text-sm flex items-center gap-1.5">
                    <Trash2 className="h-4 w-4 text-red-600" />
                    CLEARED & RESET TO ₹0.00 (Test Transactions)
                  </p>
                  <p className="text-[11px] text-slate-500">
                    These test / erroneous transactions will be purged:
                  </p>
                  <ul className="space-y-1.5 text-slate-700 pt-1">
                    <li className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                      <strong>All Test Attendance records</strong> (Clinic & Home)
                    </li>
                    <li className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                      <strong>All Test Payments & Ledger accounts</strong> (Reset to ₹0)
                    </li>
                    <li className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                      <strong>All Test Home Visit logs & Deposits</strong>
                    </li>
                    <li className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                      <strong>All Test Expenses & Settlements</strong>
                    </li>
                    <li className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                      <strong>All Test Follow-ups & Notifications</strong>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" /> Confirm Fresh Start Reset
            </DialogTitle>
            <DialogDescription className="text-xs">
              This action will reset test attendance, payments, ledger, and follow-ups. All registered patients and staff accounts will remain safe.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 text-xs">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-1 text-red-800">
              <p className="font-bold">Are you sure you want to perform a Fresh Start?</p>
              <p>
                An automatic backup snapshot will be recorded before clearing. All patient account balances will be reset to ₹0.00.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">
                To confirm, type <span className="font-bold text-red-600 font-mono">RESET</span> in the box below:
              </Label>
              <Input
                type="text"
                placeholder="Type RESET here"
                value={resetConfirmInput}
                onChange={(e) => setResetConfirmInput(e.target.value)}
                className="font-mono text-center font-bold tracking-widest text-red-700"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleExecuteReset}
              disabled={resetting || resetConfirmInput.trim().toUpperCase() !== "RESET"}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {resetting ? "Resetting data..." : "Execute Fresh Start"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
